import { ConferenceNotFoundError } from '@/error/ConferenceNotFoundError';
import { ParticipantAudioStreamListener } from '@/event/AudioStreamListener.ts';
import { SessionEventListener } from '@/event/SessionEventListener';
import i18n from '@/lib/i18n';
import { manageError } from '@/lib/manage-error.ts';
import {
  AnonymousParticipant,
  Me,
  Participant,
  PRESENTING_USER_SUFFIX,
  PresentingParticipant,
} from '@/model/Participant.ts';
import { User } from '@/model/User';
import { authService } from '@/service/AuthService.ts';
import { deviceService } from '@/service/DeviceService';
import { useConferenceStore } from '@/store/ConferenceStore';
import { resetAllStores } from '@/store/create-disposable-store.ts';
import { useDeviceStore } from '@/store/DeviceStore';
import { useSettingsStore } from '@/store/SettingsStore';
import { ConferencePageState, useUIStore } from '@/store/UIStore.ts';
import intermind, {
  AIMessage,
  ChatMessage,
  HistoryRecord,
  Participant as IntermindParticipant,
  Language,
  ListConfig,
  ListParams,
  ListResponse,
  ParticipantRole,
  TranscriptWord,
  VoiceAssistantProvider,
  VoiceAssistantState,
  Command,
} from 'intermind';
import { Session } from 'mind-sdk-web/lib/Session';

export interface ConferenceService {
  createConferenceRoom(): Promise<string | null>;
  createParticipant(roomId: string): Promise<IntermindParticipant | null>;
  updateParticipantRole(participant: Participant, role: ParticipantRole): Promise<void>;
  updateParticipantLanguage(participant: Participant, language: Language): Promise<void>;
  deleteConference(conferenceId: string): Promise<void>;
  createAnonymousParticipant(roomId: string, name: string): Promise<IntermindParticipant | null>;
  getUserByParticipantId(id: string): Promise<User | null>;
  getUsersByParticipantIds(ids: string[]): Promise<Record<string, User | null>>;
  expelParticipant(participantId: string): Promise<void>;
  muteParticipant(participantId: string): Promise<void>;
  setHandRaised(conferenceId: string, participantId: string, isRaised: boolean): Promise<void>;
  join(participant: IntermindParticipant): Promise<void>;
  closeSession(): void;
  isParticipantMe(participantId: string): boolean;
  toggleParticipantPinning(participantId: string): void;
  toggleParticipantIgnoreVideoFlag(participantId: string): void;
  toggleMyVideoFloating(): void;
  toggleRecording(): Promise<void>;
  createRecordingToken(conferenceId: string): Promise<string>;
  togglePresenting(): Promise<void>;
  approveJoinRequest(joinRequestId: string): Promise<void>;
  rejectJoinRequest(joinRequestId: string): Promise<void>;
  getHistoryListConfig(): Promise<ListConfig>;
  listHistory(params: ListParams): Promise<ListResponse<HistoryRecord>>;
  shareConferenceHistoryRecord(conferenceId: string): Promise<string>;
  importConferenceHistoryRecord(token: string): Promise<HistoryRecord | null>;
  getChatMessages(roomId: string): Promise<ChatMessage[]>;
  sendAIMessage(roomId: string, aiModel: string, message: string): Promise<void>;
  getAIMessages(roomId: string): Promise<AIMessage[]>;
  getAIModels(): Promise<string[]>;
  getTranscript(conferenceId: string): Promise<TranscriptWord[]>;
  getVoiceAssistantState(conferenceId: string): Promise<VoiceAssistantState>;
  enableVoiceAssistant(conferenceId: string): Promise<void>;
  disableVoiceAssistant(conferenceId: string): Promise<void>;
  interruptVoiceAssistant(conferenceId: string): Promise<void>;
  changeVoiceAssistantProvider(conferenceId: string, provider: VoiceAssistantProvider): Promise<boolean>;
}

export class ConferenceServiceImpl implements ConferenceService {
  private _deviceStoreUnsubscribe: (() => void) | null = null;

  async createConferenceRoom(): Promise<string | null> {
    try {
      return await intermind.conference.room.create();
    } catch (e) {
      manageError(e, i18n.t('errors.conference.createRoom'));
    }

    return null;
  }

  async createParticipant(roomId: string): Promise<IntermindParticipant | null> {
    const language = useSettingsStore.getState().language;
    try {
      const apiParticipant = await intermind.conference.participant.create({
        roomId,
        language: language || Language.ENGLISH,
      });

      return apiParticipant;
    } catch (e) {
      manageError(e, i18n.t('errors.conference.createParticipant'));
    }

    return null;
  }

  async updateParticipantRole(participant: Participant, role: ParticipantRole): Promise<void> {
    try {
      const isAuthorized = await authService.isAuthorized();
      if (isAuthorized) {
        await intermind.conference.participant.update({ participantId: participant.getId(), role });
      } else {
        await intermind.conference.participant.updateAnonymous({ participantId: participant.getId(), role });
      }

      const participantToUpdate = useConferenceStore
        .getState()
        .participants.find((p) => p.getId() === participant.getId());
      if (participantToUpdate) {
        participantToUpdate.setRole(role);
        useConferenceStore.getState().updateParticipant(participantToUpdate);
      }
    } catch (e) {
      manageError(e, i18n.t('errors.conference.updateParticipantRole'));
    }
  }

  async updateParticipantLanguage(participant: Participant, language: Language): Promise<void> {
    try {
      const isAuthorized = await authService.isAuthorized();
      if (isAuthorized) {
        await intermind.conference.participant.update({ participantId: participant.getId(), language });
      } else {
        await intermind.conference.participant.updateAnonymous({ participantId: participant.getId(), language });
      }
    } catch (e) {
      manageError(e, i18n.t('errors.conference.updateParticipantLanguage'));
    }
  }

  async deleteConference(conferenceId: string): Promise<void> {
    await intermind.conference.delete(conferenceId);
  }

  async createAnonymousParticipant(roomId: string, name: string): Promise<IntermindParticipant | null> {
    const language = useSettingsStore.getState().language;

    const anonymousUser = await authService.getAnonymousUser();
    const anonymousAuthToken = anonymousUser?.token;

    if (!anonymousAuthToken || anonymousUser?.roomId !== roomId || anonymousUser?.name !== name) {
      await authService.createAnonymousAuthToken(roomId, name);
    }

    const participant = await intermind
      .withOptions({ bypassErrorHandler: true }) // It will be handled by ConferenceErrorBoundary
      .conference.participant.createAnonymous({ roomId, language: language || Language.ENGLISH });
    return participant;
  }

  async getUserByParticipantId(participantId: string): Promise<User | null> {
    const userDto = await intermind.conference.participant.getUser(participantId);
    return userDto ? new User(userDto.id, userDto.email ?? '', userDto.firstName, userDto.lastName, '') : null;
  }

  async getUsersByParticipantIds(participantIds: string[]): Promise<Record<string, User | null>> {
    const usersDtoMap = await intermind.conference.participant.getUsers(participantIds);

    const result: Record<string, User | null> = {};

    for (const [participantId, userDto] of Object.entries(usersDtoMap)) {
      result[participantId] = userDto
        ? new User(userDto.id, userDto.email ?? '', userDto.firstName, userDto.lastName, '')
        : null;
    }

    return result;
  }

  async expelParticipant(participantId: string): Promise<void> {
    await intermind.conference.participant.expel(participantId);
  }

  async muteParticipant(participantId: string): Promise<void> {
    await intermind.conference.participant.mute(participantId);
  }

  async setHandRaised(conferenceId: string, participantId: string, isRaised: boolean): Promise<void> {
    await intermind.conference.raisedHand.update(conferenceId, participantId, isRaised);
  }

  async join(apiParticipant: IntermindParticipant): Promise<void> {
    const session = await this.initializeSession(apiParticipant);
    if (!session) {
      throw new ConferenceNotFoundError();
    }

    session.setListener(new SessionEventListener(this));

    const mindConference = session.getConference();

    await deviceService.setupPrimaryMediaStream();
    await deviceService.setupSecondaryMediaStream();

    const mindParticipants = session.getConference()?.getParticipants() || [];
    mindParticipants?.forEach((participant) =>
      participant.getMediaStream().addAudioConsumer(new ParticipantAudioStreamListener(participant.getId()))
    );

    // Recording
    useUIStore.getState().setRecordingEnabled(mindConference.isRecording());

    // Screen presenting
    const presentingParticipants = mindParticipants.filter((p) => p.isStreamingSecondaryVideo());

    // Prepare conference
    const conferenceStore = useConferenceStore.getState();
    const conference = apiParticipant.conference;
    const conferenceCreatorId = conference.createdBy;
    conferenceStore.setConference({ ...conference });
    conferenceStore.setSession(session);

    // Prepare participants
    const users = await this.getUsersByParticipantIds(mindParticipants.map((p) => p.getId()));
    const participants = mindParticipants.map((p) => {
      const user = users[p.getId()];
      const participant = user ? new Participant(p) : new AnonymousParticipant(p);
      participant.setConferenceCreator(user?.id === conferenceCreatorId);
      return participant;
    });

    // Setup up my participant
    const mindMe = mindConference.getMe();
    const me = new Me(mindMe);

    const profileLanguage = useSettingsStore.getState().language;
    if (apiParticipant.language !== profileLanguage) {
      this.updateParticipantLanguage(me, profileLanguage).catch((error) => {
        manageError(error, i18n.t('errors.conference.updateParticipantLanguage'));
      });
    }

    const user = await authService.getUser();
    me.setConferenceCreator(user?.id === conferenceCreatorId);
    await this.setupMediaStreams(me);

    conferenceStore.addParticipants([
      me,
      ...participants,
      ...presentingParticipants.map((p) => new PresentingParticipant(p)),
    ]);

    // Asynchronously mark participant's raised hands
    intermind.conference.raisedHand
      .list(apiParticipant.conference.id)
      .then((raisedHands) => {
        raisedHands.forEach((rh) => {
          if (rh.participantId === mindMe.getId()) {
            me.setHandRaisedAt(rh.raisedAt);
            conferenceStore.updateParticipant(me);
          } else {
            const p = participants.find((p) => p.getId() === rh.participantId);
            if (p) {
              p.setHandRaisedAt(rh.raisedAt);
              conferenceStore.updateParticipant(p);
            }
          }
        });
      })
      .catch(console.error);

    const voiceAssistantState = await this.getVoiceAssistantState(apiParticipant.conference.id);
    conferenceStore.setVoiceAssistantState(voiceAssistantState);

    const isAuthorizedUser = await authService.isAuthorized();
    if (isAuthorizedUser) {
      const messages = await intermind.conference.chat.list(apiParticipant.conference.id);
      useUIStore.getState().addMessages(messages);
    }
  }

  private async setupMediaStreams(me: Me): Promise<void> {
    if (!me) return;

    const primaryMediaStream = useDeviceStore.getState().primaryMediaStream;
    if (primaryMediaStream) {
      me.setMediaStream(primaryMediaStream);
    }

    const secondaryMediaStream = useDeviceStore.getState().secondaryMediaStream;
    if (secondaryMediaStream) {
      me.setSecondaryMediaStream(secondaryMediaStream);
    }

    // Subscribe to primary and secondary media streams changes
    this._deviceStoreUnsubscribe = useDeviceStore.subscribe((state, prevState) => {
      if (state.primaryMediaStream && prevState.primaryMediaStream !== state.primaryMediaStream && me) {
        me.setMediaStream(state.primaryMediaStream);

        useConferenceStore.getState().updateParticipant(me);
      }

      if (state.secondaryMediaStream && prevState.secondaryMediaStream !== state.secondaryMediaStream && me) {
        me.setSecondaryMediaStream(state.secondaryMediaStream);

        useConferenceStore.getState().updateParticipant(me);
      }
    });
  }

  private async initializeSession(apiParticipant: IntermindParticipant): Promise<Session> {
    if (!apiParticipant || !apiParticipant.token) {
      throw new Error('Participant or token is not defined');
    }

    const baseUrl = import.meta.env.VITE_APP_BASE_URL;
    const mindAppId = apiParticipant.appId;
    const conferenceId = apiParticipant.conference.id;
    const conferenceUrl = `${baseUrl}/${mindAppId}/${conferenceId}`;
    return await intermind.conference.join(conferenceUrl, apiParticipant.token);
  }

  closeSession(): void {
    const session = useConferenceStore.getState().session;
    if (session) {
      intermind.conference.closeSession(session);
    }

    useDeviceStore.getState().microphone?.release();
    useDeviceStore.getState().camera?.release();
    useDeviceStore.getState().screen?.release();
    useDeviceStore.getState().setScreenPresentingEnabled(false);

    if (this._deviceStoreUnsubscribe) {
      this._deviceStoreUnsubscribe();
      this._deviceStoreUnsubscribe = null;
    }

    resetAllStores();
    useUIStore.getState().setConferencePageState(ConferencePageState.LEAVING);
  }

  isParticipantMe(participantId: string): boolean {
    const session = useConferenceStore.getState().session;
    const me = session?.getConference()?.getMe();
    return me?.getId() === participantId;
  }

  toggleParticipantPinning(participantId: string): void {
    const participants = useConferenceStore.getState().participants;
    const participant = participants.find((p) => p.getId() === participantId);
    if (participant) {
      participant.setPinned(!participant.isPinned());
      useConferenceStore.getState().updateParticipant(participant);
    }
  }

  toggleParticipantIgnoreVideoFlag(participantId: string): void {
    const participants = useConferenceStore.getState().participants;
    const participant = participants.find((p) => p.getId() === participantId);
    if (participant) {
      participant.setIgnoreVideo(!participant.shouldIgnoreVideo());
      useConferenceStore.getState().updateParticipant(participant);
    }
  }

  toggleMyVideoFloating(): void {
    const participants = useConferenceStore.getState().participants;
    const me = participants.find((p) => p.isMe()) as Me;
    if (me) {
      me.setVideoFloating(!me.isVideoFloating());
      useConferenceStore.getState().updateParticipant(me);
    }
  }

  async toggleRecording(): Promise<void> {
    const conference = useConferenceStore.getState().conference;
    const session = useConferenceStore.getState().session;
    const recordingEnabled = useUIStore.getState().recordingEnabled;
    if (!conference || !session) {
      return;
    }

    if (recordingEnabled) {
      await intermind.conference.recording.stop(conference.roomId);
      session?.fireOnConferenceRecordingStopped(session.getConference());
    } else {
      await intermind.conference.recording.start(conference.roomId);
      session?.fireOnConferenceRecordingStarted(session.getConference());
    }
  }
  async createRecordingToken(conferenceId: string): Promise<string> {
    return await intermind.conference.recording.createToken(conferenceId);
  }

  async togglePresenting(): Promise<void> {
    const { screenPresentingEnabled, screen, setScreenPresentingEnabled } = useDeviceStore.getState();
    const conferenceStore = useConferenceStore.getState();
    const me = conferenceStore.participants.find((p) => p.isMe()) as Me;
    const myPresentingParticipant = conferenceStore.participants.find(
      (p) => p.isPresentingParticipant() && p.getId() == me.getId() + PRESENTING_USER_SUFFIX
    );

    try {
      if (!screen || screen.isAcquiring()) return;

      if (screenPresentingEnabled) {
        screen.release();
        if (myPresentingParticipant) {
          conferenceStore.removeParticipant(myPresentingParticipant.getId());
        }
      } else {
        screen.setAudioCapturing(true);
        await screen.acquire();

        if (me) {
          conferenceStore.addParticipant(new PresentingParticipant(me.getMindParticipant()!));
        }
      }

      setScreenPresentingEnabled(!screenPresentingEnabled);
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        console.warn('User denied screen presenting.');
      } else {
        manageError(error, i18n.t('errors.device.toggleScreenPresenting'));
      }
    }
  }

  async approveJoinRequest(joinRequestId: string): Promise<void> {
    await intermind.conference.joinRequest.approve(joinRequestId);
  }

  async rejectJoinRequest(joinRequestId: string): Promise<void> {
    await intermind.conference.joinRequest.reject(joinRequestId);
  }

  async getHistoryListConfig(): Promise<ListConfig> {
    return (await intermind.conference.history.listConfig()) || { filters: [], sorts: [] };
  }

  async listHistory(params: ListParams): Promise<ListResponse<HistoryRecord>> {
    return (await intermind.conference.history.list(params)) || { items: [], total: 0 };
  }

  async shareConferenceHistoryRecord(conferenceId: string): Promise<string> {
    return (await intermind.conference.history.share(conferenceId)) || '';
  }

  async importConferenceHistoryRecord(token: string): Promise<HistoryRecord | null> {
    return await intermind.conference.history.import(token);
  }

  async getChatMessages(conferenceId: string): Promise<ChatMessage[]> {
    return (await intermind.conference.chat.list(conferenceId)) || [];
  }

  async sendAIMessage(conferenceId: string, aiModel: string, message: string): Promise<void> {
    await intermind.ai.sendMessage(conferenceId, aiModel, message);
  }

  async getAIMessages(roomId: string): Promise<AIMessage[]> {
    return (await intermind.ai.listMessages(roomId)) || [];
  }

  async getAIModels(): Promise<string[]> {
    return (await intermind.ai.listModels()) || [];
  }

  async getTranscript(conferenceId: string): Promise<TranscriptWord[]> {
    return (await intermind.conference.transcript.get(conferenceId)) || [];
  }

  async getVoiceAssistantState(conferenceId: string): Promise<VoiceAssistantState> {
    return await intermind.assistant.state(conferenceId);
  }

  async enableVoiceAssistant(conferenceId: string): Promise<void> {
    const provider = useSettingsStore.getState().voiceAssistantProvider;
    await intermind.assistant.enable(conferenceId, provider);
  }

  async disableVoiceAssistant(conferenceId: string): Promise<void> {
    await intermind.assistant.disable(conferenceId);
  }

  async interruptVoiceAssistant(conferenceId: string): Promise<void> {
    await intermind.assistant.interrupt(conferenceId);
  }

  async changeVoiceAssistantProvider(conferenceId: string, provider: VoiceAssistantProvider): Promise<boolean> {
    try {
      await intermind.assistant.changeProvider(conferenceId, provider);
      return true;
    } catch (e) {
      manageError(e, i18n.t('errors.conference.changeVoiceAssistantProvider'));
    }

    return false;
  }

  async getCommand(commandType: Command, conferenceId: string) {
    return await intermind.conference.commands.getCommand(commandType, conferenceId);
  }

  async setCommand({
    commandType,
    conferenceId,
    participantId,
    isRaised,
  }: {
    commandType: Command;
    conferenceId: string;
    participantId: string;
    isRaised: boolean;
  }): Promise<void> {
    await intermind.conference.commands.update({ commandType, conferenceId, participantId, isRaised });
  }
}

export const conferenceService = new ConferenceServiceImpl();
